import type { CollectionItem } from '@chakra-ui/react';
import { Box, Portal, Select as ChakraSelect, Text } from '@chakra-ui/react';
import { Search, X } from 'lucide-react';
import * as React from 'react';

import { IconButton } from '../button';
import { TextField } from '../text-field';

const FitContentContext = React.createContext(false);

export type SelectRootProps<T extends CollectionItem = CollectionItem> = ChakraSelect.RootProps<T> & {
  /** Shrink the select to fit its content instead of filling the parent width. */
  fitContent?: boolean;
};

const fitContentStyles: React.CSSProperties = {
  width: 'fit-content',
};

const SelectRoot = React.forwardRef(function SelectRoot(
  props: SelectRootProps & { ref?: React.Ref<HTMLDivElement> },
  ref: React.Ref<HTMLDivElement>
) {
  const { value, positioning, fitContent, style, ...rest } = props;

  return (
    <ChakraSelect.Root
      {...rest}
      ref={ref}
      colorPalette="brand"
      lazyMount
      positioning={{ sameWidth: !fitContent, offset: { mainAxis: 4 }, ...positioning }}
      value={value ?? []}
      variant="outline"
      style={fitContent ? { ...fitContentStyles, ...style } : style}
    >
      <FitContentContext.Provider value={!!fitContent}>
        {props.asChild ? (
          props.children
        ) : (
          <>
            <ChakraSelect.HiddenSelect />
            {props.children}
          </>
        )}
      </FitContentContext.Provider>
    </ChakraSelect.Root>
  );
// Double cast needed: forwardRef returns ChakraSelect.RootComponent which lacks our
// custom `fitContent` prop. We widen to `unknown` first, then narrow to a generic
// callable that preserves collection-item inference for consumers.
}) as unknown as (<T extends CollectionItem>(
  props: SelectRootProps<T> & React.RefAttributes<HTMLDivElement>
) => React.JSX.Element);

export interface SelectTriggerProps extends ChakraSelect.ControlProps {
  clearable?: boolean;
  /** Leading icon displayed before the value text (see Figma `leadingIcon` slot). */
  icon?: React.ReactNode;
}

// Inline style (not Chakra css prop) — raw value needed to reserve space for the chevron indicator
const fitContentTriggerStyle: React.CSSProperties = { paddingRight: 32 };

const SelectTrigger = React.forwardRef<HTMLButtonElement, SelectTriggerProps>(
  function SelectTrigger(props, ref) {
    const { children, clearable, icon, style, ...rest } = props;
    const isFitContent = React.useContext(FitContentContext);

    return (
      <ChakraSelect.Control {...rest}>
        <ChakraSelect.Trigger ref={ref} style={isFitContent ? { ...fitContentTriggerStyle, ...style } : style}>
          {icon && (
            <Box color="neutral.250" display="flex" alignItems="center" flexShrink={0}>
              {icon}
            </Box>
          )}
          {children}
        </ChakraSelect.Trigger>
        <ChakraSelect.IndicatorGroup>
          {clearable && <SelectClearTrigger />}
          <ChakraSelect.Indicator />
        </ChakraSelect.IndicatorGroup>
      </ChakraSelect.Control>
    );
  }
);

const SelectClearTrigger = React.forwardRef<HTMLButtonElement, ChakraSelect.ClearTriggerProps>(
  function SelectClearTrigger(props, ref) {
    return (
      <ChakraSelect.ClearTrigger asChild {...props} ref={ref}>
        <IconButton aria-label="Clear selection" icon={X} size="sm" variant="ghost" />
      </ChakraSelect.ClearTrigger>
    );
  }
);

export interface SelectContentProps extends ChakraSelect.ContentProps {
  portalled?: boolean;
  portalRef?: React.RefObject<HTMLElement | null>;
  /** Render a search input above the list. */
  searchable?: boolean;
  /** Controlled search value (requires `searchable`). */
  searchValue?: string;
  /** Called when the search input value changes. */
  onSearchChange?: (value: string) => void;
  /** Placeholder text for the search input. */
  searchPlaceholder?: string;
}

const SelectContent = React.forwardRef<HTMLDivElement, SelectContentProps>(function SelectContent(
  props,
  ref
) {
  const {
    portalled = true,
    portalRef,
    searchable = false,
    searchValue,
    onSearchChange,
    searchPlaceholder = 'Search',
    children,
    ...rest
  } = props;

  return (
    <Portal container={portalRef} disabled={!portalled}>
      <ChakraSelect.Positioner>
        <ChakraSelect.Content maxWidth="100%" css={{ p: '0' }} {...rest} ref={ref}>
          {searchable && (
            <Box bg="neutral.0" p="2" position="sticky" top={0} zIndex={1}>
              <TextField
                size="sm"
                placeholder={searchPlaceholder}
                startElement={<Search />}
                value={searchValue ?? ''}
                onChange={e => onSearchChange?.(e.target.value)}
                onKeyDown={e => {
                  if (e.key === ' ') e.stopPropagation();
                  if (e.key === 'Enter') e.preventDefault();
                }}
              />
            </Box>
          )}
          <ChakraSelect.List>{children}</ChakraSelect.List>
        </ChakraSelect.Content>
      </ChakraSelect.Positioner>
    </Portal>
  );
});

const SelectItem = React.forwardRef<HTMLDivElement, ChakraSelect.ItemProps & { showIndicator?: boolean }>(
  function SelectItem(props, ref) {
    const { item, children, showIndicator = false, ...rest } = props;

    return (
      <ChakraSelect.Item justifyContent="flex-start" item={item} {...rest} ref={ref}>
        <ChakraSelect.Context>
          {({ getItemState }) => {
            const { selected } = getItemState(props);

            return (
              <>
                {showIndicator && <ChakraSelect.ItemIndicator />}
                {showIndicator && !selected ? <Box w="4" h="4" /> : undefined}
                {children ?? <ChakraSelect.ItemText />}
              </>
            );
          }}
        </ChakraSelect.Context>
      </ChakraSelect.Item>
    );
  }
);

interface SelectValueTextProps extends Omit<ChakraSelect.ValueTextProps, 'children'> {
  children?(items: CollectionItem[]): React.ReactNode;
}

const fitContentValueStyle: React.CSSProperties = { maxWidth: 'unset' };

const SelectValueText = React.forwardRef<HTMLSpanElement, SelectValueTextProps>(
  function SelectValueText(props, ref) {
    const { children, placeholder, style, ...rest } = props;
    const isFitContent = React.useContext(FitContentContext);

    return (
      <ChakraSelect.ValueText {...rest} ref={ref} style={isFitContent ? { ...fitContentValueStyle, ...style } : style}>
        <ChakraSelect.Context>
          {select => {
            const items = select.selectedItems;
            if (items.length === 0) {
              return (
                <Text as="span" color="neutral.250">
                  {placeholder}
                </Text>
              );
            }
            if (children) {
              return children(items);
            }
            if (items.length === 1) {
              return select.collection.stringifyItem(items[0]);
            }
            return `${items.length} selected`;
          }}
        </ChakraSelect.Context>
      </ChakraSelect.ValueText>
    );
  }
);

export interface SelectLabelProps extends ChakraSelect.LabelProps {
  required?: boolean;
}

const SelectLabel = React.forwardRef<HTMLLabelElement, SelectLabelProps>(function SelectLabel(props, ref) {
  const { children, required, ...rest } = props;

  return (
    <ChakraSelect.Label ref={ref} {...rest}>
      {children}
      {required && (
        <>
          {' '}
          <Text asChild color="red.500">
            <span aria-hidden="true">*</span>
          </Text>
        </>
      )}
    </ChakraSelect.Label>
  );
});

interface SelectItemGroupProps extends ChakraSelect.ItemGroupProps {
  label: React.ReactNode;
}

const SelectItemGroup = React.forwardRef<HTMLDivElement, SelectItemGroupProps>(
  function SelectItemGroup(props, ref) {
    const { children, label, ...rest } = props;

    return (
      <ChakraSelect.ItemGroup {...rest} ref={ref}>
        <ChakraSelect.ItemGroupLabel>{label}</ChakraSelect.ItemGroupLabel>
        {children}
      </ChakraSelect.ItemGroup>
    );
  }
);

export const Select = {
  Root: SelectRoot,
  Trigger: SelectTrigger,
  Content: SelectContent,
  Item: SelectItem,
  ItemText: ChakraSelect.ItemText,
  ItemGroup: SelectItemGroup,
  ValueText: SelectValueText,
  Label: SelectLabel,
};
